import * as React from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import type { LazyQueryExecFunction } from '@apollo/client';

import type { SavedProductStringTypes } from 'bundles/browse/types/SavedProductQueries';
import {
  DeleteSavedProductQuery,
  IsProductSavedQuery,
  SaveProductQuery,
} from 'bundles/unified-common/utils/restQueries';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import CourseraPlusSavedProductContext from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusSavedProductContext';
import { saveProductFactory } from 'bundles/unified-description-page-common/utils/SaveProductManager';
import type { SimpleProductRequestQueryVariables } from 'bundles/xddp/types';

type Props = {
  children: React.ReactNode;
  savedProductType: SavedProductStringTypes;
};

export type SaveProductLazyQueryFunc = LazyQueryExecFunction<{}, { input: SimpleProductRequestQueryVariables }>;

const CourseraPlusSavedProductDataProvider = ({ children, savedProductType }: Props) => {
  const courseraPlusOwnership = useCourseraPlusOwnership();
  const s12nPageData = useSpecializationPageData();
  const coursePageData = useCoursePageData();
  const productId = s12nPageData?.id || coursePageData?.id;
  const { data, loading } = useQuery<
    { IsProductSavedResponse?: { isSavedProduct?: boolean } },
    { input: SimpleProductRequestQueryVariables }
  >(IsProductSavedQuery, {
    variables: {
      input: {
        product: {
          // eslint-disable-next-line camelcase
          product_id: productId as string,
          // eslint-disable-next-line camelcase
          product_type: savedProductType,
        },
      },
    },
    skip: !courseraPlusOwnership?.isSubscribedToCPlusOrLite || !productId,
  });
  const [saveProduct] = useLazyQuery<{}, { input: SimpleProductRequestQueryVariables }>(SaveProductQuery);
  const [unSaveProduct] = useLazyQuery<{}, { input: SimpleProductRequestQueryVariables }>(DeleteSavedProductQuery);

  return (
    <CourseraPlusSavedProductContext.Provider
      value={{
        isSavedLoading: loading,
        isSaved: data?.IsProductSavedResponse?.isSavedProduct,
        saveProductManager: saveProductFactory.getConsumerSaveProduct({
          productType: savedProductType,
          saveProduct,
          unSaveProduct,
        }),
      }}
    >
      {children}
    </CourseraPlusSavedProductContext.Provider>
  );
};

export default CourseraPlusSavedProductDataProvider;
